import { toBoolean } from '@mnd-frontend/ts';
import {
  Badge,
  Flag,
  GREY_400,
  GREY_1000,
  Icons,
  MynewsdeskLogo,
  NEUTRAL_WHITE,
  Popover,
  SHADOW_1,
  SPACING_0_5,
  SPACING_1_5,
  SPACING_3,
  ScreenReaderOnly,
  isFlagCode,
  media,
} from '@mnd-frontend/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import styled from 'styled-components';
import { useMenuDataQuery } from '../../../../generated/hooks';
import mndUrl, { SubDomain } from '../../../../lib/mndUrl';
import { track } from '../../../../lib/tracker';
import HeaderPillMenu from '../../../../modules/trial/HeaderPillMenu';
import routes from '../../../../routes';
import MenuButton, { BadgeWrapper, DropdownMenu } from './MenuButton';
import MenuLink from './MenuLink';
import NavigationLink from './NavigationLink';
import { MenuItem } from './common';
import { MenuState } from './types';
import useMainMenu from './useMainMenu';

const TRIAL_PILL_TEXT = 'Trial';

const StyledMenuItem = styled(MenuItem)`
  padding: 1px 6px;
  text-align: left;
`;

const HeaderWrapper = styled.header`
  background: ${NEUTRAL_WHITE};
  box-shadow: ${SHADOW_1};
  border-bottom: 1px solid ${GREY_400};
  position: relative;
  z-index: 30;

  ${media.lessThan('desktop')} {
    .desktop-only {
      display: none;
    }
  }
  ${media.greaterThan('desktop')} {
    .mobile-only {
      display: none;
    }
  }
`;

const HeaderInner = styled.div`
  position: relative;
  display: flex;
  max-width: var(--page-width);
  margin: auto;
  height: 3.5rem;
`;

const MenuWrapper = styled.ul`
  list-style: none;
  display: grid;
  grid-auto-columns: max-content;
  grid-auto-flow: column;
  grid-gap: ${SPACING_1_5};
  margin: 0;
  padding: 0;
  flex: 1;
`;

const MobileSpaceTaker = styled.div`
  flex: 1;
  ${media.greaterThan('desktop')} {
    display: none;
  }
`;
const UserText = styled.p`
  overflow: hidden;
  max-width: 12rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin:0;

  ${Icons.AdminShield} {
    margin-left: ${SPACING_0_5};
  }
`;

const PressroomText = styled.p`
  font-size: 0.75rem;
  line-height: 1.125rem;
  overflow: hidden;
  max-width: 12rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin:0;
`;

const CircleLoadingSkeleton = styled.li`
  height: 20px;
  width: 20px;
  background: ${GREY_400};
  border-radius: 50%;
  align-self: center;
  margin: 0 0.3rem 0 0;
`;

const LoadingSkeleton = styled.li`
  height: 1rem;
  width: 5rem;
  background: ${GREY_400};
  border-radius: 0.25rem;
  align-self: center;
  margin: 0;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${SPACING_3};
  list-style: none;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  margin-left: ${SPACING_1_5};
`;

const StaticMenuButton = styled(MenuButton)`
  position: static;
`;

const TrialIndicator = styled.span`
  background: #f4f6fb;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.375rem;
  padding: 4px 8px;
  border-radius: 15px;
  &:hover {
    background: ${GREY_400};
  }
`;

const SecondaryMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  li {
    margin-bottom: 0;
  }
  ${DropdownMenu} {
    right: 0;
  }
`;

const SettingsIcon = styled(Icons.Settings)`
  color: ${GREY_1000};
`;

const PopoverContainer = styled.div`
  position: absolute;
  top: 44px;
  right: calc(25rem - 2.5rem);
  ${media.greaterThan('desktop')} {
    right: calc(25rem + 0.25rem);
  }
  z-index: 0;
  div > svg {
    transform: translateX(calc(25rem - 6rem));
  }
`;

const Header = ({ pressroomId, sourceId }: { pressroomId?: string; sourceId?: string }) => {
  const [t] = useTranslation();
  const { data, loading } = useMenuDataQuery({
    variables: {
      pressroomId: pressroomId ?? '',
      skipPressroom: !pressroomId,
      sourceId: sourceId ?? '',
      skipSource: !sourceId,
    },
  });
  const [subMenuPortalNode, setSubMenuPortalNode] = useState<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState<MenuState | null>(null);
  const [disableOutsideClick, setDisableOutsideClick] = useState(false);
  const [show, setShow] = useState(true);
  const menuOptions = useMainMenu(data);
  const subMenuRef = useRef<HTMLDivElement>(null);
  const pressrooms = data?.source?.pressrooms ?? [];
  const trial = data?.source?.trial;
  const journalistItems = [
    {
      url: mndUrl(SubDomain.WWW, `/${data?.pressroom?.siteCode ?? ''}/stories`),
      text: t('journalist.menu.stories'),
    },
    {
      url: mndUrl(SubDomain.WWW, `/${data?.pressroom?.siteCode ?? ''}/stories/alerts`),
      text: t('journalist.menu.subscriptions'),
    },
  ];
  const displayName = data?.me?.user?.name || data?.me?.user?.username;

  useEffect(() => {
    setSubMenuPortalNode(subMenuRef.current);
  }, []);

  return (
    <HeaderWrapper>
      <HeaderInner>
        {show && (
          <PopoverContainer>
            <Popover
              onDismiss={() => setShow(false)}
              badgeText={t('common_new-feature')}
              title={t('popover_toggle_navigation_title')}
              direction="up"
            >
              {t('popover_toggle_navigation_text')}
            </Popover>
          </PopoverContainer>
        )}
        <LogoWrapper>
          <LogoLink
            to={pressroomId ? routes.dashboard({ pressroomId }) : routes.pressroomSelector()}
          >
            <MynewsdeskLogo width="162" height="28" />
          </LogoLink>
          {trial && (
            <StaticMenuButton
              data-testid={'header_trial_indicator'}
              isOpen={menuOpen === MenuState.Trial}
              onClose={() => setMenuOpen(null)}
              disableOutsideClick={disableOutsideClick}
              onOpen={() => {
                setMenuOpen(MenuState.Trial);
                track({
                  event: 'Header - Open Trial pill',
                  traits: {
                    pill_text: TRIAL_PILL_TEXT,
                  },
                });
              }}
              itemGroups={[]}
              menuContent={
                <HeaderPillMenu
                  setDisableOutsideClick={setDisableOutsideClick}
                  closeTrialMenu={() => setMenuOpen(null)}
                />
              }
            >
              <TrialIndicator>{TRIAL_PILL_TEXT}</TrialIndicator>
            </StaticMenuButton>
          )}
        </LogoWrapper>
        <MenuWrapper className="desktop-only" aria-busy={loading}>
          {loading ? (
            <>
              <LoadingSkeleton />
              <LoadingSkeleton />
              <LoadingSkeleton />
              <LoadingSkeleton />
            </>
          ) : data?.me?.user?.isOnlyJournalist ? (
            <>
              {journalistItems.map(({ text, url }, i) => (
                <MenuLink href={url} key={i}>
                  {text}
                </MenuLink>
              ))}
            </>
          ) : (
            <>
              {menuOptions.map(menuOption =>
                menuOption.items.length === 1 ? (
                  <StyledMenuItem key={menuOption.menuState}>
                    <NavigationLink
                      data-testid={`header_${menuOption.menuState}`}
                      href={menuOption.items[0].url}
                      onClick={() =>
                        track({
                          event: 'Menu Navigation - Clicked',
                          traits: {
                            navigated_to: String(menuOption.heading),
                          },
                        })
                      }
                    >
                      {menuOption.heading}
                      {menuOption.badge && (
                        <BadgeWrapper>
                          <Badge variant={menuOption.badge.variant} text={menuOption.badge.text} />
                        </BadgeWrapper>
                      )}
                    </NavigationLink>
                  </StyledMenuItem>
                ) : (
                  <MenuButton
                    data-testid={`header_${menuOption.menuState}`}
                    key={menuOption.menuState}
                    isOpen={menuOpen === menuOption.menuState}
                    onClose={() => setMenuOpen(null)}
                    onOpen={() => {
                      setMenuOpen(menuOption.menuState);
                      track({
                        event: 'Menu Navigation - Clicked',
                        traits: {
                          navigated_to: String(menuOption.heading),
                        },
                      });
                    }}
                    subMenuPortalNode={subMenuPortalNode}
                    itemGroups={[
                      {
                        items: menuOption.items,
                      },
                    ]}
                  >
                    {menuOption.heading}
                    {menuOption.badge && (
                      <BadgeWrapper>
                        <Badge variant={menuOption.badge.variant} text={menuOption.badge.text} />
                      </BadgeWrapper>
                    )}
                  </MenuButton>
                ),
              )}
            </>
          )}
        </MenuWrapper>
        <MobileSpaceTaker />
        <SecondaryMenu>
          {loading ? (
            <>
              <CircleLoadingSkeleton />
              <CircleLoadingSkeleton className="mobile-only" />
              <CircleLoadingSkeleton className="mobile-only" />
              <LoadingSkeleton className="desktop-only" />
            </>
          ) : (
            <>
              <MenuButton
                data-testid={`header_${MenuState.Settings}`}
                isOpen={menuOpen === MenuState.Settings}
                onClose={() => setMenuOpen(null)}
                onOpen={() => {
                  setMenuOpen(MenuState.Settings);
                  track({
                    event: 'Menu Navigation - Clicked',
                    traits: {
                      navigated_to: 'Settings',
                    },
                  });
                }}
                itemGroups={
                  data?.pressroom && pressroomId
                    ? [
                        {
                          heading: t('menu_settings_heading'),
                          items: [
                            data?.pressroom.newsroomSettingsPage && {
                              url: routes.newsroomSettings({
                                pressroomId,
                              }),
                              activeUrl: routes.newsroomSettings.pattern,
                              text: t('menu_settings_newsroom_settings'),
                            },
                            {
                              url: routes.publishContactPeopleList({ pressroomId }),
                              text: t('menu_settings_press_contacts'),
                            },
                            {
                              url: routes.channels({ pressroomId }),
                              text: t('menu_settings_channels'),
                            },
                            data.pressroom.features.emailReportsPage && {
                              url: routes.emailReports({ pressroomId }),
                              text: t('menu_settings_email_reports'),
                            },
                            data.pressroom.features.tagManagement.enabled && {
                              url: routes.tagManagement({ pressroomId }),
                              text: t('menu_settings_category_management'),
                            },
                            data.pressroom.features.copilotFeatures && {
                              url: routes.copilotSettings({ pressroomId }),
                              text: t('menu_copilot_settings_heading'),
                            },
                          ].filter(toBoolean),
                        },
                        {
                          heading: t('menu_additional_heading'),
                          items: [
                            {
                              url: routes.emailSettings({
                                pressroomId,
                              }),
                              activeUrl: routes.emailSettings.pattern,
                              text: t('menu_additional_email'),
                            },
                            data.pressroom.features.storyWidget.enabled && {
                              url: routes.storyWidget({
                                pressroomId,
                              }),
                              activeUrl: routes.storyWidget.pattern,
                              text: t('menu_additional_story_widget'),
                            },
                          ].filter(toBoolean),
                        },
                        {
                          heading: t('menu_account_heading'),
                          items: [
                            data?.source?.features.securityPage && {
                              url: routes.accountSecurity({
                                pressroomId,
                              }),
                              text: t('menu_account_security'),
                            },
                            data?.source?.features.userManagementPage && {
                              url: routes.accountUserManagement({
                                pressroomId,
                              }),
                              text: t('menu_account_users'),
                            },
                            {
                              url: routes.accountSettings({ pressroomId }),
                              text: t('menu_account_membership'),
                            },
                          ].filter(toBoolean),
                        },
                      ]
                    : []
                }
              >
                <SettingsIcon />
                <ScreenReaderOnly>Settings</ScreenReaderOnly>
              </MenuButton>
              {pressrooms.length > 1 && data?.pressroom && (
                <MenuButton
                  data-testid={`header_${MenuState.PressroomSelector}`}
                  isOpen={menuOpen === MenuState.PressroomSelector}
                  onClose={() => setMenuOpen(null)}
                  onOpen={() => {
                    setMenuOpen(MenuState.PressroomSelector);
                    track({
                      event: 'Menu Navigation - Clicked',
                      traits: {
                        navigated_to: 'Select Newsroom',
                      },
                    });
                  }}
                  itemGroups={[
                    {
                      heading: t('menu_pressroom_heading'),
                      items: pressrooms.map(pressroom => ({
                        text: (
                          <span key={pressroom.id}>
                            <Flag
                              code={isFlagCode(pressroom.siteCode) ? pressroom.siteCode : undefined}
                              style={{ marginTop: '-2px' }}
                              size="1rem"
                            />{' '}
                            {pressroom.name}
                          </span>
                        ),
                        url: mndUrl(
                          SubDomain.Publish,
                          routes.dashboard({ pressroomId: pressroom.id }),
                        ),
                      })),
                    },
                  ]}
                >
                  <Flag
                    code={isFlagCode(data.pressroom.siteCode) ? data.pressroom.siteCode : undefined}
                    style={{ marginTop: '-2px' }}
                    size="20px"
                  />
                  <ScreenReaderOnly>Select Newsroom</ScreenReaderOnly>
                </MenuButton>
              )}
              {data?.me?.user && (
                <MenuButton
                  data-testid={`header_${MenuState.User}`}
                  isOpen={menuOpen === MenuState.User}
                  onClose={() => setMenuOpen(null)}
                  onOpen={() => {
                    setMenuOpen(MenuState.User);
                    track({
                      event: 'Menu Navigation - Clicked',
                      traits: {
                        navigated_to: 'User',
                      },
                    });
                  }}
                  itemGroups={[
                    {
                      separator: true,
                      items: [
                        !!data.pressroom?.newsroomUrl && {
                          url: data.pressroom.newsroomUrl,
                          text: t('menu_user_newsroom'),
                          newTab: true,
                        },
                        ((data.me?.user?.sources?.length ?? 0) > 1 ||
                          data.me?.user?.isMynewsdeskAdmin === true) && {
                          url: routes.pressroomSelector(),
                          text: t('menu_user_switch_source'),
                          exactMatch: true,
                        },
                        data.pressroom?.features.mention.enabled && {
                          url: mndUrl(SubDomain.Publish, `/${data.pressroom.id}/mention`),
                          newTab: true,
                          text: t('menu_user_mention'),
                        },
                        data.pressroom?.features.eqs && {
                          url: 'https://cockpit.eqs.com/ssl_nordic.html',
                          newTab: true,
                          text: t('menu_user_eqs'),
                        },
                        {
                          url: mndUrl(SubDomain.WWW, `/${data.pressroom?.siteCode ?? ''}/stories`),
                          newTab: true,
                          text: t('journalist.menu.stories'),
                        },
                      ].filter(toBoolean),
                    },
                    {
                      separator: true,
                      items: [
                        {
                          url: pressroomId
                            ? routes.userSettingsWithPressroomRoot({ pressroomId })
                            : routes.userSettingsRoot(),
                          text: t('menu_user_settings'),
                          highlight: true,
                        },
                        {
                          url: routes.external.library(data.me.user.locale),
                          newTab: true,
                          text: t('menu_user_library'),
                        },
                        {
                          url: routes.external.help(data.me.user.locale),
                          newTab: true,
                          text: t('menu_user_support'),
                        },
                      ].filter(toBoolean),
                    },
                    {
                      separator: true,
                      items: data.me?.user?.isMynewsdeskAdmin
                        ? [
                            {
                              url: mndUrl(SubDomain.WWW, '/admin'),
                              text: 'Admin',
                            },
                          ]
                        : [],
                    },
                    {
                      separator: true,
                      items: [
                        {
                          url: mndUrl(SubDomain.Publish, '/user/signout'),
                          text: t('menu_user_logout'),
                        },
                      ],
                    },
                  ]}
                >
                  <UserText className="desktop-only" title={displayName}>
                    {displayName}
                    {data.me?.user?.isMynewsdeskAdmin && <Icons.AdminShield $size="xxs" />}
                  </UserText>
                  {data?.pressroom && (
                    <PressroomText className="desktop-only" title={data.pressroom.name}>
                      {data.pressroom.name}
                    </PressroomText>
                  )}
                  <Icons.User className="mobile-only" />
                </MenuButton>
              )}
              <MenuButton
                className="mobile-only"
                data-testid={`header_${MenuState.MobileNav}`}
                isOpen={menuOpen === MenuState.MobileNav}
                onClose={() => setMenuOpen(null)}
                onOpen={() => {
                  setMenuOpen(MenuState.MobileNav);
                  track({
                    event: 'Menu Navigation - Clicked',
                    traits: {
                      navigated_to: 'Mobile navigation',
                    },
                  });
                }}
                itemGroups={
                  data?.me?.user?.isOnlyJournalist
                    ? [
                        {
                          items: journalistItems,
                        },
                      ]
                    : menuOptions
                }
              >
                <Icons.Menu />
                <ScreenReaderOnly>Mobile navigation</ScreenReaderOnly>
              </MenuButton>
            </>
          )}
        </SecondaryMenu>
      </HeaderInner>
      <div ref={subMenuRef} />
    </HeaderWrapper>
  );
};

export default Header;
